import clsx from "clsx";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useFragment, useRelayEnvironment } from "react-relay";
import { graphql, requestSubscription } from "relay-runtime";
import { Asset, Balance, BalanceAccount, isThor } from "rujira.js";
import { i18n, OmniBalance } from "rujira.ui";
import {} from "../../portfolio/utils";
import { usePreloadedAccountData } from "../../services/accountData";
import { useAccounts } from "../../services/accounts";
import {
  BalanceContextFragment$data,
  BalanceContextFragment$key,
} from "./__generated__/BalanceContextFragment.graphql";

const fragment = graphql`
  fragment BalanceContextFragment on Account {
    balancesV2(first: 100, addresses: $addresses)
      @catch
      @connection(key: "BalanceContext_balancesV2") {
      __id
      edges {
        node {
          id
          balance
          asset {
            id
            asset
            type
            chain
            metadata {
              symbol
              decimals
            }
            price {
              current
              changeDay
            }
          }
          valueUsd
          accounts {
            address
            balance
            valueUsd
            asset {
              asset
              type
              chain
              metadata {
                symbol
                decimals
              }
              variants {
                native {
                  denom
                }
              }
            }
          }
        }
      }
    }
  }
`;

const subscription = graphql`
  subscription BalanceContextSubscription(
    $connection: ID!
    $addresses: [Address!]!
  ) {
    balances(addresses: $addresses) @appendEdge(connections: [$connection]) {
      cursor
      node {
        ... on BalanceV2 {
          balance
          valueUsd
          accounts {
            balance
            valueUsd
          }
        }
      }
    }
  }
`;

export const BalanceSubscriptionProvder: FC<PropsWithChildren> = ({
  children,
}) => {
  const { accountData } = usePreloadedAccountData();
  const { selected, accounts } = useAccounts();
  const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
  // We need to filter the non-selected thorchain addresses; BalanceV2 nodes are Keyed
  // on concatenated addresses, so if extra thor1 addresses are added then we won't get updated
  const config = useMemo(() => {
    const addresses = accounts
      ?.filter((a) =>
        isThor(a.address) ? a.address === selected?.address : true
      )
      .map((a) => a.address)
      .sort();
    if (!data?.balancesV2.ok) return null;
    return {
      subscription,
      variables: { addresses, connection: data?.balancesV2?.value.__id },
    };
  }, [selected, accounts, data]);
  const env = useRelayEnvironment();
  useEffect(() => {
    if (!config?.variables.connection) return;
    const sub = requestSubscription(env, config);
    return () => {
      sub.dispose();
    };
  }, [config]);

  return children;
};

const context = createContext<BalanceResult>({
  asset: undefined as unknown as Asset,
});

type Node = NonNullable<
  NonNullable<
    NonNullable<
      Extract<
        BalanceContextFragment$data["balancesV2"],
        { ok: true }
      >["value"]["edges"]
    >[number]
  >["node"]
>;

export const BalanceProvider: FC<PropsWithChildren<{ asset: Asset }>> = ({
  asset,
  children,
}) => {
  const { accountData } = usePreloadedAccountData();
  const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
  const value = findBalance(data, asset);
  return <context.Provider value={value}>{children}</context.Provider>;
};

export const usePreloadedBalance = () => {
  return useContext(context);
};

export const usePreloadedBalanceData = () => {
  const { accountData } = usePreloadedAccountData();
  return useFragment<BalanceContextFragment$key>(fragment, accountData)
    ?.balancesV2;
};

export const usePreloadedBalances = () => {
  const data = usePreloadedBalanceData();
  if (!data?.ok) return [];
  return data.value.edges?.reduce(
    (a: Node[], v) => (v?.node ? [v.node, ...a] : a),
    []
  );
};

export const BalanceCompact: FC<{
  className?: string;
  onClick?: (a: Balance | BalanceAccount) => void;
}> = ({ className, onClick }) => {
  const { balance } = usePreloadedBalance();

  return (
    <div
      className={clsx({
        "flex condensed fs-12 color-grey": true,
        [`${className}`]: className,
      })}>
      <i18n.div className="col">Available</i18n.div>
      <OmniBalance
        balance={balance}
        onClick={onClick}
        className="condensed fs-14"
      />
    </div>
  );
};

export type BalanceResult = {
  asset: Asset & { price?: { current: bigint; changeDay?: number } };
  balance?: Balance;
  account?: BalanceAccount;
};

export const createBalanceProvider = <T extends string>(): [
  FC<
    PropsWithChildren<{
      assets: Record<T, Asset>;
    }>
  >,
  () => Record<T, BalanceResult>,
] => {
  const init = {} as Record<T, BalanceResult>;
  const context = createContext<Record<T, BalanceResult>>(init);
  const provider: FC<PropsWithChildren<{ assets: Record<T, Asset> }>> = ({
    children,
    assets,
  }) => {
    const { accountData } = usePreloadedAccountData();
    const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
    const value = (Object.entries(assets) as [T, Asset][]).reduce(
      (a, [k, v]) => ({
        ...a,
        [k]: findBalance(data, v),
      }),
      init
    );
    return <context.Provider value={value}>{children}</context.Provider>;
  };
  const consumer = () => useContext(context);
  return [provider, consumer];
};

const findBalance = (
  data: BalanceContextFragment$data | null | undefined,
  asset: Asset
): BalanceResult => {
  if (!data?.balancesV2.ok) return { asset };
  const balance = data?.balancesV2.value.edges?.find(
    (a) => a?.node?.asset?.asset === asset?.asset
  )?.node;
  if (balance)
    return {
      asset,
      balance,
      account:
        balance.accounts.find((x) => x.asset.asset === asset.asset) ||
        balance.accounts[0],
    };

  // We might explicitly need a balance for a non-unified token, eg WBTC. In that case construct a new Balance object
  const account = (data?.balancesV2.value.edges || [])
    .flatMap((a) => a?.node?.accounts || [])
    .find((a) => a.asset.asset === asset.asset);
  if (account)
    return {
      asset,
      account,
      balance: {
        asset: account.asset,
        balance: account.balance,
        accounts: [account],
        valueUsd: account.valueUsd,
      },
    };
  return { asset };
};
